/**
 * Gedeelde types + constanten tussen server.ts en client.ts.
 * Geen runtime-imports van Bun of DOM hier, zodat beide kanten dit kunnen gebruiken.
 */

/** Logische canvasgrootte: iedereen tekent in dezelfde coördinatenruimte (16:9). */
export const BOARD_WIDTH = 1600;
export const BOARD_HEIGHT = 900;

export const MIN_PEN = 2;
export const MAX_PEN = 48;
export const DEFAULT_PEN = 8;

export const MAX_NAME_LENGTH = 20;
export const MAX_CHAT_LENGTH = 300;

/** Vast palet; kleuren worden toegewezen, niet gekozen. */
export const PALETTE: readonly string[] = [
  "#ef4444", // red
  "#3b82f6", // blue
  "#22c55e", // green
  "#f97316", // orange
  "#a855f7", // purple
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#eab308", // yellow
  "#14b8a6", // teal
  "#6366f1", // indigo
  "#84cc16", // lime
  "#f43f5e", // rose
];

export interface User {
  id: string;
  name: string;
  color: string;
}

/** Punt zoals over de lijn gaat: alleen coördinaten, timing is lokaal. */
export interface WirePoint {
  x: number;
  y: number;
}

/** Stroke uit de historie voor wie later binnenkomt. */
export interface HistoryStroke {
  id: string;
  userId: string;
  color: string;
  width: number;
  points: WirePoint[];
}

/** Een GIF in de chat. De URL wijst altijd naar GIPHY; de server weigert al het andere. */
export interface GifAttachment {
  url: string;
  width: number;
  height: number;
  alt: string;
}

/** Chatbericht; naam en kleur zitten erin zodat het bericht blijft kloppen als iemand weggaat. */
export interface ChatMessage {
  userId: string;
  name: string;
  color: string;
  text: string;
  gif?: GifAttachment;
}

/** Zoekresultaat uit de GIPHY-proxy: `preview` is de kleine versie voor het raster. */
export interface GifResult extends GifAttachment {
  preview: string;
}

/** Client -> server. */
export type ClientMessage =
  | { t: "start"; id: string; x: number; y: number; w: number }
  | { t: "point"; id: string; x: number; y: number }
  | { t: "end"; id: string }
  /** Wis alleen je eigen lijnen (`mine`) of het hele bord (`all`). */
  | { t: "clear"; scope: "mine" | "all" }
  | { t: "chat"; text: string }
  /** Vraag om een nieuwe opdracht; die geldt meteen voor iedereen. */
  | { t: "newPrompt" }
  | { t: "gif"; url: string; width: number; height: number; alt: string };

/** Server -> client. */
export type ServerMessage =
  | { t: "welcome"; you: User; gifSearch: boolean }
  | { t: "users"; users: User[] }
  /** De tekenopdracht van vandaag; komt binnen bij verbinden en zodra de dag wisselt. */
  | { t: "prompt"; text: string }
  /** Gaat alleen naar wie er al was, zodat de nieuwkomer zijn eigen binnenkomst niet hoort. */
  | { t: "join"; user: User }
  | { t: "history"; strokes: HistoryStroke[]; chat: ChatMessage[] }
  | { t: "chat"; message: ChatMessage }
  /** De chat is leeggemaakt: nieuwe dag, of iedereen was de room uit. */
  | { t: "chatCleared" }
  /** Gewist: van één tekenaar, of `null` voor het hele bord. */
  | { t: "cleared"; userId: string | null }
  | { t: "start"; id: string; userId: string; x: number; y: number; w: number; color: string }
  | { t: "point"; id: string; x: number; y: number };
